import { h } from "./dom";
import { formatCents, formatDateTime } from "./format";
import { supabase, type ContributionRow } from "./supabase";

export type ContributionWithRequest = ContributionRow & {
  review_requests: {
    restaurant_name: string;
    requester_email: string;
    requester_name: string;
    tier: string;
    payment_status: string;
  } | null;
};

export type LedgerEntryOptions = {
  onEditRefund?: (entry: ContributionRow) => void;
};

export async function loadContributionsForRequest(requestId: string): Promise<{
  rows: ContributionRow[];
  error: string | null;
}> {
  const { data, error } = await supabase
    .from("contributions")
    .select("*")
    .eq("review_request_id", requestId)
    .order("created_at", { ascending: false });

  if (error) return { rows: [], error: error.message };
  return { rows: (data ?? []) as ContributionRow[], error: null };
}

export async function loadAllContributions(limit = 500): Promise<{
  rows: ContributionWithRequest[];
  error: string | null;
}> {
  const { data, error } = await supabase
    .from("contributions")
    .select(
      `
      *,
      review_requests (
        restaurant_name,
        requester_email,
        requester_name,
        tier,
        payment_status
      )
    `,
    )
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) return { rows: [], error: error.message };
  return { rows: (data ?? []) as ContributionWithRequest[], error: null };
}

export async function updateContributionRefund(
  id: string,
  refundedCents: number,
  note?: string | null,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from("contributions")
    .update({ refunded_cents: refundedCents, note: note ?? null })
    .eq("id", id);

  return { error: error?.message ?? null };
}

export async function linkContributionsToReview(
  requestId: string,
  reviewId: string,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from("contributions")
    .update({ review_id: reviewId })
    .eq("review_request_id", requestId)
    .is("review_id", null);

  return { error: error?.message ?? null };
}

export function renderLedgerList(
  entries: ContributionRow[],
  opts: LedgerEntryOptions = {},
): HTMLElement {
  if (entries.length === 0) {
    return h(
      "p",
      { class: "detail-muted" },
      "No ledger entries yet. Paid tiers create a row when Stripe checkout completes.",
    );
  }

  return h(
    "ul",
    { class: "ledger-list" },
    ...entries.map((entry) => renderLedgerEntry(entry, opts)),
  );
}

export function renderLedgerEntry(
  entry: ContributionRow,
  opts: LedgerEntryOptions = {},
): HTMLElement {
  const netCents = Math.max(0, entry.amount_cents - entry.refunded_cents);
  const processorRef = entry.processor_reference?.trim() || null;

  return h(
    "li",
    { class: "ledger-entry" },
    h(
      "div",
      { class: "ledger-entry-head" },
      h("span", { class: "ledger-amount" }, formatCents(entry.amount_cents)),
      entry.refunded_cents > 0
        ? h(
            "span",
            { class: "ledger-refund" },
            `Refunded ${formatCents(entry.refunded_cents)} · Net ${formatCents(netCents)}`,
          )
        : null,
      opts.onEditRefund
        ? h(
            "button",
            {
              class: "btn btn-ghost btn-sm ledger-edit-refund",
              type: "button",
              onclick: () => opts.onEditRefund?.(entry),
            },
            entry.refunded_cents > 0 ? "Update refund" : "Record refund",
          )
        : null,
    ),
    ledgerDetailGrid([
      ["Recorded", formatDateTime(entry.created_at)],
      ["Processor", entry.payment_processor ?? "—"],
      [
        "Reference",
        processorRef
          ? processorReferenceCell(processorRef, entry.payment_processor)
          : h("span", { class: "detail-muted" }, "—"),
      ],
      [
        "Linked review",
        entry.review_id
          ? h(
              "button",
              {
                class: "detail-link-btn",
                type: "button",
                onclick: () => {
                  window.location.hash = `/editor/${entry.review_id}`;
                },
              },
              "Open in editor",
            )
          : h("span", { class: "detail-muted" }, "Not linked yet"),
      ],
      ["Note", entry.note ?? "—"],
      ["Ledger ID", h("code", { class: "ledger-id" }, entry.id)],
    ]),
  );
}

export function openRefundModal(entry: ContributionRow, onSaved: () => void): void {
  document.querySelector(".refund-modal-backdrop")?.remove();

  let saving = false;
  const statusEl = h("p", { class: "modal-status", "aria-live": "polite" });

  const dollarsInput = h("input", {
    class: "input",
    type: "number",
    min: "0",
    max: String(entry.amount_cents / 100),
    step: "0.01",
    id: "refund-amount",
  }) as HTMLInputElement;
  dollarsInput.value = (entry.refunded_cents / 100).toFixed(2);

  const noteInput = h("textarea", {
    class: "input",
    id: "refund-note",
    rows: 3,
    placeholder: "Optional note — Stripe refund ID, reason, date…",
  }) as HTMLTextAreaElement;
  noteInput.value = entry.note ?? "";

  const close = () => {
    backdrop.remove();
    document.body.classList.remove("modal-open");
    document.removeEventListener("keydown", onKeydown);
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") close();
  };

  const saveBtn = h(
    "button",
    {
      class: "btn btn-primary",
      type: "button",
      onclick: () => void saveRefund(),
    },
    "Save refund",
  );

  async function saveRefund(): Promise<void> {
    if (saving) return;
    const dollars = Number.parseFloat(dollarsInput.value);
    if (!Number.isFinite(dollars) || dollars < 0) {
      statusEl.textContent = "Enter a valid refund amount.";
      statusEl.className = "modal-status modal-status-error";
      return;
    }
    const refundedCents = Math.round(dollars * 100);
    if (refundedCents > entry.amount_cents) {
      statusEl.textContent = `Refund cannot exceed ${formatCents(entry.amount_cents)}.`;
      statusEl.className = "modal-status modal-status-error";
      return;
    }

    saving = true;
    saveBtn.setAttribute("disabled", "true");
    statusEl.textContent = "Saving…";
    statusEl.className = "modal-status";

    const { error } = await updateContributionRefund(
      entry.id,
      refundedCents,
      noteInput.value.trim() || null,
    );

    saving = false;
    saveBtn.removeAttribute("disabled");

    if (error) {
      statusEl.textContent = error;
      statusEl.className = "modal-status modal-status-error";
      return;
    }

    entry.refunded_cents = refundedCents;
    entry.note = noteInput.value.trim() || null;
    statusEl.textContent = "Saved.";
    statusEl.className = "modal-status modal-status-ok";
    onSaved();
    window.setTimeout(close, 450);
  }

  const panel = h(
    "div",
    {
      class: "modal-panel refund-modal",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "refund-modal-title",
    },
    h(
      "header",
      { class: "modal-header" },
      h(
        "div",
        {},
        h("p", { class: "eyebrow" }, "Contribution ledger"),
        h("h2", { class: "modal-title", id: "refund-modal-title" }, "Record refund"),
        h(
          "p",
          { class: "modal-subtitle" },
          `Original amount ${formatCents(entry.amount_cents)} · update after processing in Stripe.`,
        ),
      ),
      h(
        "button",
        {
          class: "modal-close",
          type: "button",
          "aria-label": "Close",
          onclick: close,
        },
        "×",
      ),
    ),
    h(
      "div",
      { class: "modal-body" },
      h(
        "label",
        { class: "field", for: "refund-amount" },
        h("span", { class: "label" }, "Refunded amount (USD)"),
        dollarsInput,
      ),
      h(
        "label",
        { class: "field", for: "refund-note" },
        h("span", { class: "label" }, "Note"),
        noteInput,
      ),
    ),
    h(
      "footer",
      { class: "modal-footer" },
      statusEl,
      h(
        "div",
        { class: "modal-footer-actions" },
        h("button", { class: "btn btn-ghost", type: "button", onclick: close }, "Cancel"),
        saveBtn,
      ),
    ),
  );

  const backdrop = h(
    "div",
    {
      class: "modal-backdrop refund-modal-backdrop",
      onclick: (event: MouseEvent) => {
        if (event.target === backdrop) close();
      },
    },
    panel,
  );

  document.body.appendChild(backdrop);
  document.body.classList.add("modal-open");
  document.addEventListener("keydown", onKeydown);
  dollarsInput.focus();
  dollarsInput.select();
}

function processorReferenceCell(reference: string, processor: string | null): HTMLElement {
  if (processor === "stripe" && reference.startsWith("cs_")) {
    return h(
      "a",
      {
        class: "detail-link",
        href: `https://dashboard.stripe.com/checkout/sessions/${encodeURIComponent(reference)}`,
        target: "_blank",
        rel: "noopener noreferrer",
      },
      reference,
    );
  }
  return h("code", { class: "ledger-ref" }, reference);
}

type LedgerChild = Node | string | number | null;

function ledgerDetailGrid(items: [string, LedgerChild][]): HTMLElement {
  return h(
    "dl",
    { class: "detail-grid" },
    ...items.flatMap(([label, value]) => [
      h("dt", { class: "detail-label" }, label),
      h("dd", { class: "detail-value" }, value),
    ]),
  );
}

export function ledgerSummary(rows: ContributionWithRequest[]): {
  totalCents: number;
  refundedCents: number;
  netCents: number;
  count: number;
} {
  let totalCents = 0;
  let refundedCents = 0;
  for (const row of rows) {
    totalCents += row.amount_cents;
    refundedCents += row.refunded_cents;
  }
  return {
    totalCents,
    refundedCents,
    netCents: Math.max(0, totalCents - refundedCents),
    count: rows.length,
  };
}
